// Janela "Feriados" — Administrativo › Horas da equipe (13/10/2026).
//
// Pedido do Lucas: "permita que a Daniele anote feriados no sistema (como
// administrativo). se alguém trabalhar no feriado e marcar, sobrepõe ao feriado.
// a base de cálculo continua a mesma".
//
// O sistema só conhecia os feriados nacionais de data fixa. Aqui a administração
// cadastra os outros (Carnaval, Corpus Christi, feriado municipal, recesso…): o
// dia sai do esperado de todo mundo — o painel não pinta "nada anotado" e o robô
// não cobra. Quem trabalhar no feriado lança a hora normalmente e ela vale.
//
// Quem pode cadastrar e remover é o servidor que diz (`pode_editar`, permissão
// horas.feriados.editar); os demais veem o calendário. O que entra e o que fica
// de fora (fim de semana, feriado nacional) também é o servidor que decide.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App as AntApp, AutoComplete, Button, Col, DatePicker, Modal, Popconfirm, Row, Segmented, Spin, Tag, Typography } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { api } from '../operacional-horas/api';
import {
  SUGESTOES_DE_NOME, detalheDoFeriado, frasesDoCadastro, jaPassou, perguntaDeRemover, previaDoPeriodo, proximoFeriado, quandoDoFeriado,
  type CalendarioDoAno, type FeriadoDoAno, type RespostaCadastro,
} from './feriadosUtils';

const { Text } = Typography;

type Props = {
  aberto: boolean;
  onFechar: () => void;
  /** Algo foi cadastrado ou removido: o painel de trás refaz a conta. */
  onMudou: () => void;
};

export default function ModalFeriados({ aberto, onFechar, onMudou }: Props) {
  const { message } = AntApp.useApp();
  const [ano, setAno] = useState<number | null>(null);
  const [calendario, setCalendario] = useState<CalendarioDoAno | null>(null);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [dia, setDia] = useState<Dayjs | null>(null);
  const [ate, setAte] = useState<Dayjs | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Só a ÚLTIMA leitura pedida vale: trocar de ano depressa não pode deixar a
  // resposta mais lenta de um ano anterior tomar o lugar da lista na tela.
  const pedido = useRef(0);
  const carregar = useCallback(async (qualAno: number | null) => {
    const este = ++pedido.current;
    setErroLista(null);
    try {
      const d: CalendarioDoAno = await api(`/financeiro/painel/feriados${qualAno ? `?ano=${qualAno}` : ''}`);
      if (este !== pedido.current) return;
      setCalendario(d);
      setAno(d.ano);
    } catch (e: any) {
      if (este !== pedido.current) return;
      // Não consegui ler ≠ não há feriado: a lista fica sem resposta, com o motivo.
      setCalendario(null);
      setErroLista(e?.message || 'Não foi possível ler o calendário de feriados.');
    }
  }, []);

  // A cada abertura: formulário limpo e o calendário do ano corrente (o servidor diz qual é).
  useEffect(() => {
    if (!aberto) return;
    setNome(''); setDia(null); setAte(null); setErro(null); setAno(null);
    carregar(null);
  }, [aberto, carregar]);

  const hoje = calendario?.hoje || dayjs().format('YYYY-MM-DD');
  const anoBase = Number(hoje.slice(0, 4));
  const podeEditar = !!calendario?.pode_editar;
  const inicio = dia ? dia.format('YYYY-MM-DD') : '';
  const fim = ate ? ate.format('YYYY-MM-DD') : inicio;
  const proximo = calendario ? proximoFeriado(calendario.feriados, hoje) : null;

  async function cadastrar() {
    if (nome.trim().length < 3) { setErro('Dê um nome ao feriado. Ex.: Carnaval, Recesso de fim de ano.'); return; }
    if (!dia) { setErro('Escolha o dia do feriado.'); return; }
    if (ate && ate.isBefore(dia, 'day')) { setErro('O último dia tem que ser igual ou depois do primeiro.'); return; }
    setEnviando(true); setErro(null);
    try {
      const r: RespostaCadastro = await api('/financeiro/painel/feriados', {
        method: 'POST', body: JSON.stringify({ inicio, fim, nome: nome.trim() }),
      });
      const f = frasesDoCadastro(r);
      message.success(f.ok, 6);
      if (f.aviso) message.info(f.aviso, 9);
      setNome(''); setDia(null); setAte(null);
      // O feriado pode ser de outro ano (Carnaval do ano que vem): a lista vai para o ano dele.
      await carregar(Number(inicio.slice(0, 4)));
      onMudou();
    } catch (e: any) {
      // A recusa do servidor já é frase para gente (sábado, feriado nacional, ano errado…).
      setErro(e?.message || 'Não foi possível cadastrar o feriado.');
    } finally { setEnviando(false); }
  }

  async function remover(f: FeriadoDoAno) {
    try {
      const r = await api('/financeiro/painel/feriados/remover', { method: 'POST', body: JSON.stringify({ inicio: f.inicio, fim: f.fim }) });
      const n = (r.removidos || []).length;
      message.success(`"${f.nome}" removido: ${n} ${n === 1 ? 'dia voltou' : 'dias voltaram'} a contar como dia de trabalho.`);
      await carregar(ano);
      onMudou();
    } catch (e: any) { message.error(e?.message || 'Não foi possível remover.'); }
  }

  return (
    <Modal
      open={aberto} onCancel={onFechar} footer={<Button onClick={onFechar}>Fechar</Button>}
      title={<span><CalendarOutlined style={{ marginRight: 8 }} />Feriados</span>}
      width="94vw" style={{ maxWidth: podeEditar ? 980 : 620, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto', overflowX: 'hidden' } }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
        Em feriado ninguém deve hora: o dia sai do esperado de toda a equipe e o lembrete de horas não cobra.
        Quem trabalhar no feriado lança a hora normalmente — ela conta inteira, como hora a mais.
        Os feriados nacionais de data fixa já estão no calendário.
      </Text>

      <Row gutter={[28, 16]}>
        {podeEditar && (
          <Col xs={24} md={10}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Nome</Text>
            <AutoComplete
              style={{ width: '100%' }} value={nome} maxLength={80} allowClear
              placeholder="Ex.: Carnaval, Corpus Christi, Recesso de fim de ano"
              options={SUGESTOES_DE_NOME.map(v => ({ value: v }))}
              filterOption={(digitado, o) => String(o?.value || '').toLowerCase().includes(digitado.toLowerCase())}
              onChange={(v) => { setNome(v || ''); setErro(null); }}
            />

            <Row gutter={12} style={{ marginTop: 14 }}>
              <Col span={12}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Dia</Text>
                <DatePicker
                  format="DD/MM/YYYY" style={{ width: '100%' }} value={dia} placeholder="dd/mm/aaaa"
                  onChange={(v) => { setDia(v); setErro(null); if (v && ate && ate.isBefore(v, 'day')) setAte(null); }}
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Até <Text type="secondary" style={{ fontWeight: 400 }}>(se for mais de um dia)</Text></Text>
                <DatePicker
                  format="DD/MM/YYYY" style={{ width: '100%' }} value={ate} placeholder="opcional" disabled={!dia}
                  disabledDate={(d) => !!dia && d.isBefore(dia, 'day')}
                  defaultPickerValue={dia || undefined}
                  onChange={(v) => { setAte(v); setErro(null); }}
                />
              </Col>
            </Row>
            {inicio && (
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>{previaDoPeriodo(inicio, fim)}</Text>
            )}

            {erro && <Alert type="error" showIcon style={{ marginTop: 12 }} message={erro} />}
            <Button type="primary" block size="large" style={{ marginTop: 14 }} loading={enviando} onClick={cadastrar}>
              Cadastrar feriado
            </Button>
          </Col>
        )}

        <Col xs={24} md={podeEditar ? 14 : 24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <Text strong>Calendário de {ano || anoBase}</Text>
            <Segmented
              size="small" value={ano || anoBase}
              options={[anoBase - 1, anoBase, anoBase + 1].map(a => ({ value: a, label: String(a) }))}
              onChange={(v) => { setAno(Number(v)); carregar(Number(v)); }}
            />
          </div>
          {proximo && ano === anoBase && (
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
              Próximo: <Text strong style={{ fontSize: 12 }}>{proximo.nome}</Text>, {quandoDoFeriado(proximo)}.
            </Text>
          )}
          {!calendario && !erroLista && <Spin size="small" />}
          {erroLista && (
            <Alert type="warning" showIcon message="Não consegui ler o calendário de feriados" description={erroLista}
              action={<Button size="small" onClick={() => carregar(ano)}>Tentar de novo</Button>} />
          )}
          {calendario && calendario.feriados.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>Nenhum feriado neste ano.</Text>
          )}
          {(calendario?.feriados || []).map((f) => {
            const passado = jaPassou(f, hoje);
            return (
              <div key={`${f.inicio}-${f.origem}`} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                padding: '6px 0', borderBottom: '1px solid #f0f0f0', opacity: passado || f.fim_de_semana ? 0.55 : 1,
              }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'inline-block', minWidth: 150, fontVariantNumeric: 'tabular-nums' }}>{quandoDoFeriado(f)}</span>
                  <Text strong>{f.nome}</Text>
                  <Tag color={f.origem === 'nacional' ? 'default' : 'gold'} style={{ marginInlineStart: 8, fontSize: 11 }}>{detalheDoFeriado(f)}</Tag>
                </span>
                {podeEditar && f.origem === 'cadastrado' && (
                  <Popconfirm
                    title={perguntaDeRemover(f)}
                    description="O dia volta a contar como dia de trabalho: quem não anotou passa a dever essas horas."
                    okText="Remover" cancelText="Não" onConfirm={() => remover(f)}
                  >
                    <Button size="small" type="link" style={{ padding: 0 }}>remover</Button>
                  </Popconfirm>
                )}
              </div>
            );
          })}
          {calendario && !podeEditar && (
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 10 }}>
              Só quem tem a permissão de cadastrar feriados altera este calendário.
            </Text>
          )}
        </Col>
      </Row>
    </Modal>
  );
}
